import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
  sendPasswordResetEmail,
  signInAnonymously,
  User,
} from 'firebase/auth';
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  getDoc,
  setDoc,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { auth, db, googleProvider, microsoftProvider } from './firebase';
import { Teacher, TeacherSession } from '../types';
import { SchoolService } from './school.service';
import { logger } from '../utils/logger';
import { getTeacherPlatformLevel } from '../utils/platformLevel';

/**
 * Authentication service for managing user registration, login, and session management.
 * Handles Firebase Authentication and Firestore teacher profile creation/retrieval.
 */
export class AuthService {
  /**
   * Registers a new teacher account with email and password.
   * Creates both Firebase Auth user and Firestore teacher profile.
   *
   * @param email - Teacher's email address
   * @param password - Account password (min 6 characters)
   * @param displayName - Teacher's full name (will be split into firstName/lastName)
   * @returns Promise resolving to the created Teacher object
   * @throws {Error} If registration fails or Firestore document creation fails
   *
   * @example
   * ```typescript
   * const teacher = await AuthService.register(
   *   'teacher@school.com',
   *   'password123',
   *   'John Doe'
   * );
   * console.log(teacher.uid); // Firebase UID
   * ```
   */
  static async register(
    email: string,
    password: string,
    displayName: string,
    options?: { schoolName?: string; country?: string; city?: string; referralCode?: string }
  ): Promise<Teacher> {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      await updateProfile(user, { displayName });

      // Split displayName into first/last name
      const nameParts = displayName.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const teacherData: Teacher = {
        uid: user.uid,
        username: displayName,
        email,
        firstName,
        lastName,
        classes: [],
        signupDate: new Date().toISOString(),
        approved: false,
        ...(options?.schoolName ? {
          schoolName: options.schoolName,
          schoolNameNormalized: SchoolService.normalizeSchoolName(options.schoolName),
        } : {}),
        ...(options?.country ? { country: options.country } : {}),
        ...(options?.city ? { city: options.city } : {}),
        emailDomain: email.split('@')[1]?.toLowerCase() ?? '',
        settings: {
          theme: 'light',
          notifications: true,
        },
      };

      // CRITICAL: Create the Firestore document
      await setDoc(doc(db, 'teachers', user.uid), teacherData);

      // Apply referral code if provided (best-effort, non-blocking)
      if (options?.referralCode?.trim()) {
        const { ReferralService } = await import('./referral.service');
        ReferralService.applyReferralCode(options.referralCode.trim().toUpperCase(), user.uid).catch(() => {});
      }

      // Track signup event (non-blocking)
      import('./analytics.service').then(({ AnalyticsService }) => {
        AnalyticsService.track('signup', user.uid, { emailDomain: teacherData.emailDomain ?? '' });
      }).catch(() => {});

      // Notify admins if new teacher has a school email (non-blocking)
      SchoolService.notifyAdminsOfNewClusterMember(teacherData).catch(() => {});

      // Detect IP-based school cluster (non-blocking — D0 email sent by Firestore trigger)
      const fnUrlSignup = import.meta.env.VITE_CLOUD_FUNCTIONS_URL;
      if (fnUrlSignup) {
        user.getIdToken().then((token) => {
          fetch(`${fnUrlSignup}/api/track-signup-ip`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
          }).catch(() => {});
        }).catch(() => {});
      }

      return teacherData;
    } catch (error) {
      logger.error('Registration error', error instanceof Error ? error : new Error('Unknown registration error'), { email });
      throw error;
    }
  }

  /**
   * Authenticates a teacher and retrieves their profile from Firestore.
   * Creates a missing profile automatically for existing Firebase Auth users.
   *
   * @param email - Teacher's email address
   * @param password - Account password
   * @returns Promise resolving to the Teacher object with all profile data
   * @throws {Error} If authentication fails or profile retrieval fails
   *
   * @example
   * ```typescript
   * try {
   *   const teacher = await AuthService.login('teacher@school.com', 'password123');
   *   console.log(teacher.classes); // Array of class IDs
   * } catch (error) {
   *   console.error('Login failed:', error.message);
   * }
   * ```
   */
  static async login(email: string, password: string): Promise<Teacher> {
    try {
      logger.info('Attempting to sign in', { email });

      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      logger.info('User signed in successfully', { userId: user.uid });
      logger.info('Fetching teacher document from Firestore', { userId: user.uid });

      const teacherDoc = await getDoc(doc(db, 'teachers', user.uid));

      logger.info('Teacher document exists', { exists: teacherDoc.exists(), userId: user.uid });

      if (!teacherDoc.exists()) {
        logger.info('Creating missing teacher profile', { userId: user.uid });
        // If profile doesn't exist, create it now (for existing users)
        const displayName = user.displayName || user.email?.split('@')[0] || 'Teacher';
        const nameParts = displayName.split(' ');

        const teacherData: Teacher = {
          uid: user.uid,
          username: displayName,
          email: user.email || email,
          firstName: nameParts[0] || '',
          lastName: nameParts.slice(1).join(' ') || '',
          classes: [],
          signupDate: new Date().toISOString(),
          trialStartDate: new Date().toISOString(),
          emailDomain: (user.email || email).split('@')[1]?.toLowerCase() ?? '',
          settings: {
            theme: 'light',
            notifications: true,
          },
        };

        await setDoc(doc(db, 'teachers', user.uid), teacherData);
        logger.info('Teacher profile created successfully', { userId: user.uid });
        return teacherData;
      }

      const teacherData = teacherDoc.data() as Teacher;
      logger.info('Teacher data retrieved successfully', { userId: user.uid, username: teacherData.username });

      // Block login if account is pending admin approval
      if (teacherData.approved === false) {
        await signOut(auth);
        throw new Error('Your account is pending admin approval. You will receive access once it has been reviewed.');
      }

      // Track last login timestamp (non-blocking — must not gate the login flow)
      const now = new Date().toISOString();
      const prevLastLogin = teacherData.lastLogin;
      updateDoc(doc(db, 'teachers', user.uid), { lastLogin: now }).catch(() => {});
      teacherData.lastLogin = now;

      // Platform score: +1 per calendar day; annual reset preserving peak level (non-blocking)
      const todayDate = now.split('T')[0];
      const lastLoginDate = prevLastLogin?.split('T')[0];
      if (lastLoginDate !== todayDate) {
        const currentYear = new Date().getFullYear();
        const scoreYear = teacherData.platformScoreYear ?? currentYear;
        if (scoreYear < currentYear) {
          // Annual reset: preserve peak level, start fresh score
          const currentLevel = getTeacherPlatformLevel(teacherData.platformScore ?? 0).level;
          const newPeak = Math.max(currentLevel, teacherData.platformLevelPeak ?? 1);
          updateDoc(doc(db, 'teachers', user.uid), {
            platformScore: 1,
            platformScoreYear: currentYear,
            platformLevelPeak: newPeak,
          }).catch(() => {});
          teacherData.platformScore = 1;
          teacherData.platformScoreYear = currentYear;
          teacherData.platformLevelPeak = newPeak;
        } else {
          const oldScore = teacherData.platformScore ?? 0;
          const newScore = oldScore + 1;
          const updates: any = { platformScore: newScore };
          if (!teacherData.platformScoreYear) updates.platformScoreYear = currentYear;
          // Check for level-up and write notification (non-blocking)
          const oldLevel = getTeacherPlatformLevel(oldScore).level;
          const newLevel = getTeacherPlatformLevel(newScore);
          if (newLevel.level > oldLevel) {
            const notif = {
              id: `notif-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
              type: 'level_up' as const,
              message: `You reached Platform Level ${newLevel.level} — ${newLevel.emoji} ${newLevel.name}! Perk: ${newLevel.perk}`,
              createdAt: new Date().toISOString(),
              read: false,
            };
            updates.notifications = arrayUnion(notif);
          }
          updateDoc(doc(db, 'teachers', user.uid), updates).catch(() => {});
          teacherData.platformScore = newScore;
          if (!teacherData.platformScoreYear) teacherData.platformScoreYear = currentYear;
        }
      }

      // Set trialStartDate on first login if not yet set (non-blocking)
      if (!teacherData.trialStartDate) {
        const trialStartDate = new Date().toISOString();
        updateDoc(doc(db, 'teachers', user.uid), { trialStartDate }).catch(() => {});
        teacherData.trialStartDate = trialStartDate;
      }

      // Non-blocking: re-check pending referral milestones on every login
      import('./classroom.service').then(({ ClassroomService }) => {
        ClassroomService.getTeacherClassrooms(user.uid).then((classrooms) => {
          import('./referral.service').then(({ ReferralService }) => {
            ReferralService.checkAndActivatePendingReferral(user.uid, classrooms).catch(() => {});
          });
        }).catch(() => {});
      }).catch(() => {});

      // Register device session (non-blocking)
      AuthService.writeSession(user.uid).catch(() => {});

      // Track login event (non-blocking)
      import('./analytics.service').then(({ AnalyticsService }) => {
        AnalyticsService.track('login', user.uid);
      }).catch(() => {});

      // Check and send pending email sequence (D2/D5/D10/D15) — fire-and-forget
      const fnUrl = import.meta.env.VITE_CLOUD_FUNCTIONS_URL;
      if (fnUrl) {
        user.getIdToken().then((token) => {
          fetch(`${fnUrl}/api/email/check-sequence`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
          }).catch(() => {});
        }).catch(() => {});
      }

      return teacherData;
    } catch (error) {
      logger.error('Login error', error instanceof Error ? error : new Error('Unknown login error'), { email });

      const firebaseError = error as { code?: string; message?: string };
      switch (firebaseError.code) {
        case 'auth/invalid-credential':
        case 'auth/wrong-password':
        case 'auth/user-not-found':
          throw new Error('Invalid email or password. Please check your credentials and try again.');
        case 'auth/too-many-requests':
          throw new Error('Too many failed login attempts. Please wait a few minutes before trying again.');
        case 'auth/user-disabled':
          throw new Error('This account has been disabled. Please contact support.');
        case 'permission-denied':
          throw new Error('Authentication error. Please try signing in again or contact support if the issue persists.');
        default:
          if (firebaseError.message?.toLowerCase().includes('permission')) {
            throw new Error('Authentication error. Please try signing in again or contact support if the issue persists.');
          }
          throw error;
      }
    }
  }

  /**
   * Sends a password reset email to the specified email address.
   * Provides user-friendly error messages for common failure scenarios.
   *
   * @param email - Email address to send reset link to
   * @returns Promise that resolves when email is sent successfully
   * @throws {Error} With user-friendly message if email send fails
   *
   * @example
   * ```typescript
   * await AuthService.resetPassword('teacher@school.com');
   * // User will receive an email with reset link
   * ```
   */
  static async resetPassword(email: string): Promise<void> {
    try {
      const appOrigin = (import.meta.env['VITE_APP_URL'] as string | undefined) || window.location.origin;
      await sendPasswordResetEmail(auth, email, {
        url: `${appOrigin}/portal`,
        handleCodeInApp: false,
      });
    } catch (error) {
      logger.error('Password reset error', error instanceof Error ? error : new Error('Unknown password reset error'), { email });

      // Provide user-friendly error messages
      const firebaseError = error as { code?: string };
      switch (firebaseError.code) {
        case 'auth/user-not-found':
          throw new Error('No account found with this email address.');
        case 'auth/invalid-email':
          throw new Error('Please enter a valid email address.');
        case 'auth/too-many-requests':
          throw new Error('Too many password reset attempts. Please try again later.');
        default:
          throw new Error('Unable to send password reset email. Please try again.');
      }
    }
  }

  /**
   * Signs out the current user and cleans up their localStorage data.
   * Removes user-specific data like schedules and calendar events.
   *
   * @returns Promise that resolves when logout is complete
   * @throws {Error} If logout process fails
   *
   * @example
   * ```typescript
   * await AuthService.logout();
   * // User is signed out and redirected to login
   * ```
   */
  /**
   * Marks the onboarding wizard as completed for a teacher.
   * Called after the teacher finishes or skips the first-login wizard.
   */
  static async completeOnboarding(uid: string): Promise<void> {
    await updateDoc(doc(db, 'teachers', uid), { onboardingCompleted: true });
  }

  static async logout(): Promise<void> {
    try {
      // Get current user before signing out
      const currentUser = auth.currentUser;

      if (currentUser) {
        // Remove device session from Firestore (non-blocking)
        const sessionId = sessionStorage.getItem('sessionId');
        if (sessionId) {
          AuthService.removeSession(currentUser.uid, sessionId).catch(() => {});
          sessionStorage.removeItem('sessionId');
        }

        // Clear user-specific localStorage data
        const userDataKeys = [
          `classroom-schedules-${currentUser.uid}`,
          `active-schedule-id-${currentUser.uid}`,
          `calendar-events-${currentUser.uid}`,
        ];

        userDataKeys.forEach((key) => {
          localStorage.removeItem(key);
        });
      }

      // Sign out from Firebase
      await signOut(auth);
    } catch (error) {
      logger.error('Logout error', error instanceof Error ? error : new Error('Unknown logout error'));
      throw error;
    }
  }

  /**
   * Removes deprecated global localStorage keys from older versions.
   * Should be called once during app initialization to migrate old data.
   *
   * @example
   * ```typescript
   * AuthService.cleanupOldGlobalData();
   * ```
   */
  static cleanupOldGlobalData(): void {
    const oldGlobalKeys = ['classroom-schedules', 'active-schedule-id', 'calendar-events'];

    oldGlobalKeys.forEach((key) => {
      if (localStorage.getItem(key)) {
        logger.info('Removing old global data', { key });
        localStorage.removeItem(key);
      }
    });
  }

  /**
   * Creates a Firestore teacher profile for existing Firebase Auth users.
   * Used for migration or when profile was not created during registration.
   *
   * @param user - Firebase User object
   * @returns Promise resolving to the newly created Teacher profile
   * @throws {Error} If profile creation fails
   *
   * @internal This is primarily used internally by the login method
   */
  static async createMissingProfile(user: User): Promise<Teacher> {
    const displayName = user.displayName || user.email?.split('@')[0] || 'Teacher';
    const nameParts = displayName.split(' ');

    const teacherData: Teacher = {
      uid: user.uid,
      username: displayName,
      email: user.email || '',
      firstName: nameParts[0] || '',
      lastName: nameParts.slice(1).join(' ') || '',
      classes: [],
      signupDate: new Date().toISOString(),
      settings: {
        theme: 'light',
        notifications: true,
      },
    };

    await setDoc(doc(db, 'teachers', user.uid), teacherData);
    return teacherData;
  }

  /**
   * Retrieves the teacher profile for a Firebase Auth user.
   * Creates profile if it doesn't exist (for backward compatibility).
   *
   * @param user - Firebase User object
   * @returns Promise resolving to Teacher profile, or null if fetch fails
   *
   * @example
   * ```typescript
   * const user = auth.currentUser;
   * if (user) {
   *   const teacher = await AuthService.getCurrentTeacher(user);
   *   console.log(teacher?.classes);
   * }
   * ```
   */
  static async getCurrentTeacher(user: User): Promise<Teacher | null> {
    try {
      const teacherDoc = await getDoc(doc(db, 'teachers', user.uid));

      if (!teacherDoc.exists()) {
        // Create missing profile for existing users
        logger.info('Creating missing teacher profile', { userId: user.uid });
        return await this.createMissingProfile(user);
      }

      return teacherDoc.data() as Teacher;
    } catch (error) {
      logger.error('Error fetching teacher', error instanceof Error ? error : new Error('Unknown error'), { userId: user.uid });
      return null;
    }
  }

  /**
   * Grants admin privileges to a user by email address.
   * One-time setup operation - requires existing admin access to execute.
   *
   * @param userEmail - Email address of the user to grant admin access
   * @returns Promise that resolves when admin access is granted
   * @throws {Error} If user is not found or update fails
   *
   * @example
   * ```typescript
   * await AuthService.grantAdminAccess('newadmin@school.com');
   * console.log('Admin access granted successfully');
   * ```
   */
  static async grantAdminAccess(userEmail: string): Promise<void> {
    try {
      // First, find the user by email
      const usersQuery = query(collection(db, 'teachers'), where('email', '==', userEmail));
      const usersSnapshot = await getDocs(usersQuery);

      if (usersSnapshot.empty) {
        throw new Error('User not found');
      }

      const userDoc = usersSnapshot.docs[0];
      await updateDoc(doc(db, 'teachers', userDoc.id), {
        isAdmin: true,
        role: 'admin',
        lastModified: new Date().toISOString(),
      });

      logger.info('Admin access granted', { userEmail, userId: userDoc.id });
    } catch (error) {
      logger.error('Error granting admin access', error instanceof Error ? error : new Error('Unknown error'), { userEmail });
      throw error;
    }
  }

  /**
   * Revokes admin privileges from a user.
   * Resets user role to 'teacher' and removes admin flag.
   *
   * @param userId - Firebase UID of the user to revoke admin access from
   * @returns Promise that resolves when admin access is revoked
   * @throws {Error} If update fails
   *
   * @example
   * ```typescript
   * await AuthService.revokeAdminAccess('user-firebase-uid');
   * ```
   */
  /**
   * Signs in anonymously via Firebase Auth for student sessions.
   * Returns the Firebase anonymous UID to store in the student's account record.
   */
  static async signInStudentAnonymously(): Promise<string> {
    const credential = await signInAnonymously(auth);
    return credential.user.uid;
  }

  static async revokeAdminAccess(userId: string): Promise<void> {
    try {
      await updateDoc(doc(db, 'teachers', userId), {
        isAdmin: false,
        role: 'teacher',
        lastModified: new Date().toISOString(),
      });
    } catch (error) {
      logger.error('Error revoking admin access', error instanceof Error ? error : new Error('Unknown error'), { userId });
      throw error;
    }
  }

  /**
   * Checks if a user has admin privileges.
   * Verifies both isAdmin flag and role field.
   *
   * @param userId - Firebase UID to check
   * @returns Promise resolving to true if user is admin, false otherwise
   *
   * @example
   * ```typescript
   * const isAdmin = await AuthService.isCurrentUserAdmin(auth.currentUser.uid);
   * if (isAdmin) {
   *   // Show admin panel
   * }
   * ```
   */
  static async isCurrentUserAdmin(userId: string): Promise<boolean> {
    try {
      const userDoc = await getDoc(doc(db, 'teachers', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return userData.isAdmin === true || userData.role === 'admin';
      }
      return false;
    } catch (error) {
      logger.error('Error checking admin status', error instanceof Error ? error : new Error('Unknown error'), { userId });
      return false;
    }
  }

  // ── OAuth ──────────────────────────────────────────────────────────────

  static async signInWithGoogle(): Promise<Teacher> {
    const result = await signInWithPopup(auth, googleProvider);
    return AuthService._handleOAuthResult(result);
  }

  static async signInWithMicrosoft(): Promise<Teacher> {
    const result = await signInWithPopup(auth, microsoftProvider);
    return AuthService._handleOAuthResult(result);
  }

  private static async _handleOAuthResult(result: any): Promise<Teacher> {
    const user = result.user as User;
    const ref = doc(db, 'teachers', user.uid);
    const snap = await getDoc(ref);

    if (snap.exists()) {
      const teacherData = snap.data() as Teacher;
      // Block if pending approval
      if (teacherData.approved === false) {
        await signOut(auth);
        throw new Error('Your account is pending admin approval.');
      }
      // Update lastLogin
      const now = new Date().toISOString();
      updateDoc(ref, { lastLogin: now }).catch(() => {});
      teacherData.lastLogin = now;
      // Register session
      AuthService.writeSession(user.uid).catch(() => {});
      return teacherData;
    }

    // New user — create profile
    const displayName = user.displayName || user.email?.split('@')[0] || 'Teacher';
    const nameParts = displayName.trim().split(' ');
    const now = new Date().toISOString();
    const teacherData: Teacher = {
      uid: user.uid,
      username: displayName,
      email: user.email || '',
      firstName: nameParts[0] || '',
      lastName: nameParts.slice(1).join(' ') || '',
      classes: [],
      signupDate: now,
      trialStartDate: now,
      emailDomain: (user.email || '').split('@')[1]?.toLowerCase() ?? '',
      settings: { theme: 'light', notifications: true },
    };
    await setDoc(ref, teacherData);
    AuthService.writeSession(user.uid).catch(() => {});
    return teacherData;
  }

  // ── Session management ─────────────────────────────────────────────────

  private static parseUA(ua: string): { browser: string; os: string } {
    const os = /Windows/.test(ua) ? 'Windows'
      : /Mac/.test(ua) ? 'macOS'
      : /Android/.test(ua) ? 'Android'
      : /iPhone|iPad/.test(ua) ? 'iOS'
      : /Linux/.test(ua) ? 'Linux' : 'Unknown';
    const browser = /Edg/.test(ua) ? 'Edge'
      : /OPR|Opera/.test(ua) ? 'Opera'
      : /Chrome/.test(ua) ? 'Chrome'
      : /Safari/.test(ua) ? 'Safari'
      : /Firefox/.test(ua) ? 'Firefox' : 'Unknown';
    return { os, browser };
  }

  static async writeSession(uid: string): Promise<void> {
    const sessionId = crypto.randomUUID();
    sessionStorage.setItem('sessionId', sessionId);
    const now = new Date().toISOString();
    const { os, browser } = AuthService.parseUA(navigator.userAgent);
    const session: TeacherSession = { id: sessionId, browser, os, loginAt: now, lastSeen: now };

    const ref = doc(db, 'teachers', uid);
    const snap = await getDoc(ref);
    const existing: TeacherSession[] = (snap.data()?.activeSessions ?? []) as TeacherSession[];
    const trimmed = [...existing, session].slice(-5);
    await updateDoc(ref, { activeSessions: trimmed });
  }

  static async removeSession(uid: string, sessionId: string): Promise<void> {
    const ref = doc(db, 'teachers', uid);
    const snap = await getDoc(ref);
    const sessions: TeacherSession[] = (snap.data()?.activeSessions ?? []) as TeacherSession[];
    const target = sessions.find(s => s.id === sessionId);
    if (target) {
      await updateDoc(ref, { activeSessions: arrayRemove(target) });
    }
  }

  static async refreshSessionLastSeen(uid: string): Promise<void> {
    const sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) return;
    const ref = doc(db, 'teachers', uid);
    const snap = await getDoc(ref);
    const sessions: TeacherSession[] = (snap.data()?.activeSessions ?? []) as TeacherSession[];
    const updated = sessions.map(s =>
      s.id === sessionId ? { ...s, lastSeen: new Date().toISOString() } : s
    );
    await updateDoc(ref, { activeSessions: updated });
  }
}
